import * as React from 'react';
import { createPortal } from 'react-dom';
import { useScroll } from '@react-three/drei';
import type { SceneKey } from './ScrollSceneController';
import { clamp01 } from './utils';
import { makeDecksVisState, updateDecksVis } from './decksVisibility';
import { StackedChevrons } from '../ui/StackedChevrons';
import { Diamond } from '../ui/Diamond';
import { Button } from '../ui/Button';
import { Price } from '../ui/Price';
import { ReviewCard } from '../ui/ReviewCard';
import { ReviewCardSkeleton } from '../ui/ReviewCardSkeleton';
import { Modal } from '../ui/Modal';
import { ReviewForm } from '../ui/ReviewForm';
import { ServiceModules } from './ServiceModules';
import { ServiceBody } from './ServiceBody';
import { AboutTimeline } from "./AboutTimeline";
import { BookingModal } from '../booking/BookingModal';
import { useDragScroll } from '../../hooks/useDragScroll';
import { useCarouselEdges } from '../../hooks/useCarouselEdges';
import { CarouselArrow } from '../ui/CarouselArrow';
import { Pagination } from '../ui/Pagination';
import { useReviewsQuery } from '../../hooks/useReviews';
import { serviceLabel } from '../../lib/reviewsFormat';
import { useContent, text, usdRate } from '../../hooks/useContent';
import { useTranslation } from 'react-i18next';
import { formatAmount, currencySymbol, usdAmount, formatUsd } from '../../utils/formatPrice';
import { useSceneScalars } from '../../hooks/useSceneScalars';
import { useBreakpoint } from '../../hooks/useBreakpoint';


type Props = {
  activeScene: SceneKey;
  onNavigate?: (page: string) => void;
  /** Router navigate for regular routes (e.g. /reviews). A raw <a href>
   *  would bypass the app basename (when one is set) and 404. */
  onRouteNavigate?: (path: string) => void;
  /** Owned by parent so the portaled CTA disappears on page-level unmount;
   *  drei's <Scroll html> keeps its inner ReactDOM root alive after the home
   *  tree unmounts, which would otherwise leak the button onto /decks. */
  portalHost?: HTMLDivElement | null;
};

export function ScrollSections({ activeScene, onNavigate, onRouteNavigate, portalHost }: Props) {
  const { t } = useTranslation();
  const scroll = useScroll();
  const scalars = useSceneScalars();
  const breakpoint = useBreakpoint();
  const scrollIndicatorRef = React.useRef<HTMLAnchorElement>(null);
  const homeHeroRef = React.useRef<HTMLDivElement>(null);
  const decksButtonRef = React.useRef<HTMLDivElement>(null);
  const decksTitleRef = React.useRef<HTMLDivElement>(null);
  const servicesWrapRef = React.useRef<HTMLDivElement>(null);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [reviewSubmitted, setReviewSubmitted] = React.useState(false);
  const [bookingKey, setBookingKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    const el = scroll?.el as HTMLElement | undefined;
    if (!el) return;
    const update = () => {
      const max = el.scrollHeight - el.clientHeight;
      const offset = max > 0 ? el.scrollTop / max : 0;

      const indicator = scrollIndicatorRef.current;
      if (indicator) {
        // Fade out fast as the user starts scrolling. Outside home, force 0.
        const FADE_FRACTION = 0.01;
        const k = activeScene === 'home' ? Math.min(1, offset / FADE_FRACTION) : 1;
        const opacity = Math.max(0, 1 - k);
        indicator.style.opacity = String(opacity);
        indicator.style.pointerEvents = opacity < 0.05 ? 'none' : 'auto';
      }

      const hero = homeHeroRef.current;
      if (hero) {
        // Hero text fades across t∈[0, HERO_FADE_END_T]. That end sits below
        // GATE_HIDE_T (0.35), so scrolling up the title only eases in after the
        // arch is already back in frame.
        const HERO_FADE_END = scalars.thresholds.heroFadeEnd / scalars.tScale;
        const k = activeScene === 'home' ? Math.min(1, offset / HERO_FADE_END) : 1;
        hero.style.opacity = String(Math.max(0, 1 - k));
      }
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    return () => el.removeEventListener('scroll', update);
  }, [scroll, activeScene, scalars]);

  // Decks text crossfade — shares the exact same visibility model as the 3D
  // boxes (decksVisibility.ts) in t-space, so text and boxes stay in lockstep.
  const decksVisWindow = React.useMemo(
    () => ({
      entryStart: scalars.thresholds.decksDwell,
      entryEnd: scalars.thresholds.decksEnterEnd,
      exitStart: scalars.thresholds.decksReviews,
      exitEnd: scalars.tMax,
      reverseExitStart: scalars.thresholds.decksReverseExit,
    }),
    [scalars.thresholds, scalars.tMax],
  );

  // Decks CTA + title crossfade — driven by the shared visibility model
  // (decksVisibility.ts) so text tracks the 3D boxes exactly. RAF-driven so
  // progress keeps flowing when the scroll pauses.
  React.useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const visState = makeDecksVisState();
    const tick = (now: number) => {
      const delta = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = (scroll?.offset ?? 0) * scalars.tScale;

      // Services copy fades in/out in lockstep with the FBO cross-fade rather
      // than on a fixed-duration CSS toggle: presence ramps with the
      // home→services window on entry and the services→decks window on exit.
      // smoothstep(presence) reproduces 1 - smoothstep(progression), the same
      // blend curve TransitionMaterial uses, so the text tracks its own 3D
      // background instead of popping out while the scene is still mid-glide.
      const servicesWrap = servicesWrapRef.current;
      if (servicesWrap) {
        let presence: number;
        if (t <= scalars.thresholds.homeServices) {
          presence = clamp01(t / scalars.thresholds.homeServices);
        } else if (t < scalars.thresholds.servicesDecks) {
          presence = 1;
        } else {
          presence =
            1 -
            clamp01(
              (t - scalars.thresholds.servicesDecks) /
                (scalars.thresholds.decksDwell - scalars.thresholds.servicesDecks),
            );
        }
        // Mobile: services run as a tall readable scroll column over the
        // scene (min-h-screen sections), so the copy stays fully visible
        // instead of cross-fading per the desktop FBO choreography.
        const eased =
          breakpoint === 'mobile' ? 1 : presence * presence * (3 - 2 * presence);
        servicesWrap.style.opacity = String(eased);
        servicesWrap.style.pointerEvents = eased > 0.5 ? 'auto' : 'none';
      }

      const decksVisValue = updateDecksVis(visState, t, delta, decksVisWindow);
      const decksScale = 0.94 + 0.06 * decksVisValue;
      const button = decksButtonRef.current;
      if (button) {
        button.style.opacity = String(decksVisValue);
        button.style.transform = `translate(-50%, 0) scale(${decksScale})`;
        button.style.pointerEvents = decksVisValue > 0.5 ? 'auto' : 'none';
      }
      const title = decksTitleRef.current;
      if (title) {
        title.style.opacity = String(decksVisValue);
        title.style.transform = `scale(${decksScale})`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [scroll, scalars, decksVisWindow, breakpoint]);

  const { data: content } = useContent();
  const rate = usdRate(content);

  const services = React.useMemo(
    () =>
      (content?.services ?? []).map((s, i) => {
        const [sessionNum, ...rest] = s.duration.split(' ');
        return {
          n: String(i + 1),
          key: s.key,
          title: s.title,
          titleMobile: s.titleMobile ?? '',
          titleTone: 'white' as const,
          subtitle: s.subtitle,
          body: s.body,
          priceCur: currencySymbol(s.currency),
          priceNum: formatAmount(s.price),
          priceUnit: s.currency,
          priceUsd: usdAmount(s.price, rate) ?? undefined,
          sessionNum,
          sessionUnit: rest.join(' '),
          format: s.format ?? '',
          formatLabel: s.formatLabel ?? '',
          modules: s.modules ?? [],
          ctaLabel: s.ctaLabel,
        };
      }),
    [content, rate],
  );

  const bookingService = React.useMemo(
    () => content?.services.find((x) => x.key === bookingKey) ?? null,
    [content, bookingKey],
  );

  const reviewsQuery = useReviewsQuery();
  const reviews = React.useMemo(
    () =>
      (reviewsQuery.data ?? []).slice(0, 6).map((r) => ({
        name: r.name,
        role: serviceLabel(r.service),
        body: r.body,
        rating: r.rating,
        avatarInitial: r.name
          .replace(/[^A-Za-z]/g, '')
          .slice(0, 1)
          .toUpperCase(),
      })),
    [reviewsQuery.data],
  );

  const carouselRef = React.useRef<HTMLDivElement>(null);
  useDragScroll(carouselRef);
  const { canScrollLeft, canScrollRight, scrollByCard, activeIndex, count, scrollToIndex } =
    useCarouselEdges(carouselRef);

  const servicesVisible = activeScene === 'services';
  const decksVisible = activeScene === 'decks';

  return (
    <main className="w-screen pointer-events-auto">
      <header className="sr-only">
        <h1>Dennis Valchez — Psychic, Medium &amp; Tarot Reader in Sydney</h1>
        <p>
          Live tarot, psychic and mediumship readings on Zoom and in person in Sydney, Australia.
          Get clarity on love, career, family and life&apos;s crossroads. Booking via Stripe;
          sessions from $200 AUD.
        </p>
      </header>

      <section id="home" className="relative h-screen px-6 pt-28">
        <div
          ref={homeHeroRef}
          // Mobile: the title sits just above the gate arch (the arch crown
          // projects to ≈30% of the CANVAS height — fixed per-tier camera —
          // so the two-line block starts at 20svh and ends right over it)
          // and the tagline is pushed to the bottom (mt-auto) to sit just
          // above the scroll indicator; the divider is md+ only. The canvas
          // (fixed inset-0) matches the SMALL viewport on phones whose
          // browser chrome is visible at load, so both the box height and
          // the pad use svh, not vh; vh stays as the fallback where svh is
          // unsupported. md+: original bottom-anchored stack (tagline,
          // divider, title).
          className="absolute inset-x-0 top-0 h-screen supports-[height:1svh]:h-[100svh] flex flex-col items-center pt-[20vh] supports-[padding-top:1svh]:pt-[20svh] md:bottom-0 md:h-auto md:justify-end md:pt-0 md:pb-[14vh] px-6 text-center pointer-events-none"
        >
          <div
            className="t-subtitle-blue t-subtitle--hero-fit order-2 mt-auto pb-40 md:order-none md:mt-0 md:pb-0"
            style={{ textShadow: '0 0 14px rgba(255, 255, 255, 0.7)' }}
          >
            {text(content, 'hero.tagline')}
          </div>
          <div
            className="my-5 hidden h-px w-16 md:block"
            style={{ background: 'var(--primary)', opacity: 0.55 }}
          />
          <h2 className="t-hero t-hero--on-cloud t-hero--home-fit order-1 m-0 whitespace-pre-line md:order-none">
            {`${text(content, 'hero.title.line1')}\n${text(content, 'hero.title.line2')}`}
          </h2>
        </div>
        <StackedChevrons
          ref={scrollIndicatorRef}
          href="#services"
          tone="primary"
          aria-label={t('scrollDownToServices')}
          // Mobile: the section is 100vh tall (the LARGE viewport on iOS), so
          // bottom-10 lands under the browser bar at load — anchor to the
          // small viewport instead: (100vh − 100svh) reaches the svh bottom,
          // +3rem clears it. Falls back to bottom-10 where svh is unsupported.
          className="scroll-indicator absolute left-1/2 bottom-10 mobile:supports-[bottom:1svh]:bottom-[calc(100vh_-_100svh_+_3rem)] z-10 -translate-x-1/2"
        />
      </section>

      <div
        ref={servicesWrapRef}
        data-scroll-anchor="services"
        style={{ opacity: 0, willChange: 'opacity' }}
      >
        {services.map((s, idx) => {
          return (
            <section
              key={s.n}
              id={idx === 0 ? 'services' : undefined}
              // Deep-link target (e.g. the reading-copy email CTA): resolved
              // by useScrollToHash against the measured anchors. Inert for
              // the choreography — deriveHomeThresholds reads named keys only.
              data-scroll-anchor={`service-${s.key}`}
              aria-hidden={!servicesVisible}
              // lg:px-6 only — below lg the inner t-container alone sets the
              // side gutters, so content aligns flush with the navbar pill.
              className="min-h-screen lg:px-6 pt-16 pb-20 md:pt-24 md:pb-0"
            >
              <h2 className="sr-only">
                {s.title.replace(/\n/g, ' ')} — {s.priceCur}
                {s.priceNum} {s.priceUnit}
                {s.priceUsd != null ? ` (${formatUsd(s.priceUsd)})` : ''}
              </h2>
              <div className="t-container">
                {/* Alternate flush-left / flush-right inside t-container.
                  t-container is a plain block (not grid), so justify-self
                  here is a no-op and the card collapses to whatever default
                  margins it picks — use ml-auto / mr-auto for real block
                  alignment. */}
                <div
                  className={['flex flex-col max-w-xl', idx % 2 === 0 ? 'mr-auto' : 'ml-auto'].join(
                    ' ',
                  )}
                >
                  <Diamond n={s.n} tone="white" />

                  <div className="mt-8 md:mt-10">
                    <div className="t-section whitespace-pre-line text-white">
                      {breakpoint === 'mobile' && s.titleMobile ? s.titleMobile : s.title}
                    </div>
                    <div className="t-subtitle mt-4">{s.subtitle}</div>

                    <ServiceBody body={s.body} />

                    {s.modules.length > 0 ? (
                      <ServiceModules
                        modules={s.modules}
                        portalHost={portalHost}
                        ctaLabel={s.ctaLabel}
                        onCta={() => setBookingKey(s.key)}
                      />
                    ) : null}

                    <div className="service-foot max-w-2xl">
                      <div className="service-meta-row">
                        <div className="stat flex flex-col items-start gap-2">
                          <span className="t-stat-label">
                            {text(content, 'service.stat.price')}
                          </span>
                          <Price size="lg" inverse cur={s.priceCur} unit={s.priceUnit} usd={s.priceUsd}>
                            {s.priceNum}
                          </Price>
                        </div>
                        {s.format ? (
                          <div className="stat flex flex-col items-start gap-2">
                            <span className="t-stat-label">
                              {s.formatLabel || text(content, 'service.stat.format')}
                            </span>
                            <span className="flex flex-1 items-center max-w-[280px] text-[17px] font-light leading-snug text-white">
                              {s.format}
                            </span>
                          </div>
                        ) : (
                          <div className="stat flex flex-col items-start gap-2">
                            <span className="t-stat-label">
                              {text(content, 'service.stat.session')}
                            </span>
                            <span className="t-stat-value">
                              {s.sessionNum}
                              <span className="t-stat-ctx">{s.sessionUnit}</span>
                            </span>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="solid"
                        surface="dark"
                        as="button"
                        type="button"
                        onClick={() => setBookingKey(s.key)}
                      >
                        {s.ctaLabel}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Decks section — layout placeholder; 3D Text title lives in DecksPart,
          and the CTA is portaled to <body> as a fixed overlay (sticky inside a
          tall parent unpins after the first 100vh, so it scrolled away once
          the camera arrived in DECKS).
          Height of this placeholder = the decks dwell length: every decks
          threshold (sceneThresholds.ts HOME_CHOREO) is derived from this
          section's measured anchor and the #reviews anchor, so making it
          taller/shorter stretches/compresses the whole decks choreography —
          no constants to re-sync. Mobile keeps the 200vh base. */}
      <section
        id="decks"
        data-scroll-anchor="decks"
        aria-hidden={!decksVisible}
        className={[
          'h-[200vh] md:h-[320vh] transition-opacity duration-300',
          decksVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      >
        <h2 className="sr-only">{t('tarotDecksUsed')}</h2>
      </section>

      <section
        id="reviews"
        data-scroll-anchor="reviews"
        className="h-screen flex flex-col items-center justify-center px-6 bg-white"
      >
        <header className="text-center">
          <div className="t-eyebrow-blue">{text(content, 'reviews.eyebrow')}</div>
          <h2 className="t-display mt-3 text-primary">{text(content, 'reviews.heading')}</h2>
        </header>

        {/* w-full: as a flex item the container would otherwise size to its
            (wider-than-viewport) carousel content, capping scrollLeft short of
            the last cards on mobile. */}
        <div className="relative t-container w-full mt-12">
          <div
            ref={carouselRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory cursor-grab active:cursor-grabbing -mx-12 md:mx-0 pl-6 pr-12 md:px-6 pb-2 scroll-px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {reviewsQuery.isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <ReviewCardSkeleton key={`sk-${i}`} variant="compact" />
                ))
              : reviews.map((r) => (
                  <ReviewCard
                    key={r.name}
                    name={r.name}
                    role={r.role}
                    body={r.body}
                    rating={r.rating}
                    avatarInitial={r.avatarInitial}
                  />
                ))}
          </div>

          {/* Edge fades — white-to-transparent gradient masks. Pointer-events
              none so they never eat clicks on the cards underneath. Desktop
              only: on mobile the snapped card sits at the page gutter, so the
              fade would wash out its text (dots + card peek hint instead). */}
          <div
            aria-hidden
            className={[
              'hidden md:block pointer-events-none absolute inset-y-0 left-0 w-24',
              'bg-gradient-to-r from-white to-transparent',
              'transition-opacity duration-200',
              canScrollLeft ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />
          <div
            aria-hidden
            className={[
              'hidden md:block pointer-events-none absolute inset-y-0 right-0 w-24',
              'bg-gradient-to-l from-white to-transparent',
              'transition-opacity duration-200',
              canScrollRight ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />

          {/* Arrow overlays — hidden on touch viewports (drag-swipe is enough). */}
          <div className="hidden md:block absolute left-3 top-1/2 -translate-y-1/2 z-10">
            <CarouselArrow
              direction="prev"
              onClick={() => scrollByCard(-1)}
              disabled={!canScrollLeft}
            />
          </div>
          <div className="hidden md:block absolute right-3 top-1/2 -translate-y-1/2 z-10">
            <CarouselArrow
              direction="next"
              onClick={() => scrollByCard(1)}
              disabled={!canScrollRight}
            />
          </div>
        </div>

        {!reviewsQuery.isLoading && count > 1 ? (
          <div className="mt-6 md:hidden">
            <Pagination
              variant="dots"
              page={Math.min(activeIndex, count - 1) + 1}
              totalPages={count}
              onGo={(n) => scrollToIndex(n - 1)}
            />
          </div>
        ) : null}

        <div className="mt-12 flex items-center gap-4">
          <Button variant="solid" surface="light" onClick={() => onRouteNavigate?.('/reviews')}>
            {text(content, 'home.reviews.seeMore')}
          </Button>
          <Button
            variant="outline"
            surface="light"
            onClick={() => {
              setReviewSubmitted(false);
              setReviewOpen(true);
            }}
          >
            {text(content, 'home.reviews.leaveReview')}
          </Button>
        </div>
      </section>

      {/* About Me timeline — parked-camera tail, before the Footer */}
      <AboutTimeline />

      <Modal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        eyebrow={reviewSubmitted ? undefined : 'Reviews'}
        title={reviewSubmitted ? undefined : 'Leave a review'}
        lede={
          reviewSubmitted
            ? undefined
            : 'Truth, not flattery. A few sentences about what came through.'
        }
        size="compact"
        container={portalHost ?? null}
      >
        <ReviewForm
          onSuccess={() => setReviewSubmitted(true)}
          onClose={() => setReviewOpen(false)}
        />
      </Modal>

      <BookingModal
        service={bookingService}
        open={bookingKey !== null}
        onClose={() => setBookingKey(null)}
        portalHost={portalHost}
      />

      {portalHost
        ? createPortal(
            <div
              ref={decksButtonRef}
              className="fixed left-1/2 bottom-[6.5rem] z-10 pointer-events-none"
              style={{
                opacity: 0,
                transform: 'translate(-50%, 0) scale(0.94)',
                transformOrigin: 'center center',
                willChange: 'opacity, transform',
              }}
            >
              <Button variant="readmore" surface="light" onClick={() => onNavigate?.('decks')}>
                {text(content, 'home.decks.readMore')}
              </Button>
            </div>,
            portalHost,
          )
        : null}
      {portalHost
        ? createPortal(
            <div
              ref={decksTitleRef}
              aria-hidden={!decksVisible}
              className="fixed left-0 right-0 top-[16vh] z-10 px-6 pointer-events-none"
              style={{
                opacity: 0,
                transform: 'scale(0.94)',
                transformOrigin: 'center center',
                willChange: 'opacity, transform',
              }}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="t-eyebrow-blue t-subtitle--on-cloud">
                  {text(content, 'decks.overlay.eyebrow')}
                </div>
                <div className="h-px w-24 bg-primary/40" />
                <h2 className="t-hero t-hero--on-cloud t-hero--decks-fit text-center">
                  {text(content, 'decks.overlay.title')}
                </h2>
              </div>
            </div>,
            portalHost,
          )
        : null}
    </main>
  );
}
